// hooks/useAuth.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      if (userStr && token) {
        const userData = JSON.parse(userStr);
        setUser(userData);
      }
    } catch (error) {
      console.error('Erreur chargement utilisateur:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch('https://josephsbusiness.com/api/auth/login.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        // Vérifier si le 2FA est requis
        if (data.requires_2fa) {
          setTwoFactorRequired(true);
          setTwoFactorData({
            userId: data.user_id,
            emailMasked: data.email_masked
          });
          return { 
            success: true, 
            requiresTwoFactor: true,
            twoFactorData: {
              userId: data.user_id,
              emailMasked: data.email_masked
            }
          };
        }
        
        // Connexion normale sans 2FA
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        return { success: true, user: data.user };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Erreur login:', error);
      return { success: false, message: 'Erreur de connexion au serveur' };
    }
  };

// Dans useAuth.js, assurez-vous que verifyTwoFactorCode remet twoFactorRequired à false
const verifyTwoFactorCode = async (userId, code) => {
  try {
    const response = await fetch('https://josephsbusiness.com/api/auth/verify_2fa.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, code }),
    });

    const data = await response.json();

    if (data.success) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      setTwoFactorRequired(false); // ✅ Important !
      setTwoFactorData(null);
      return { success: true, user: data.user };
    } else {
      return { success: false, message: data.message || 'Code invalide' };
    }
  } catch (error) {
    console.error('❌ Erreur:', error);
    return { success: false, message: 'Erreur de connexion' };
  }
};
  const cancelTwoFactor = () => {
    setTwoFactorRequired(false);
    setTwoFactorData(null);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setTwoFactorRequired(false);
    setTwoFactorData(null);
    navigate('/login');
  };

  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    if (user.role === 'Super Administrateur') return true;
    return user.permissions.includes(permission);
  };

  const getChurchLogo = () => {
    if (!user) return null;
    return user.logo_parent || null;
  };

  const resendTwoFactorCode = async (userId) => {
  try {
    const response = await fetch('https://josephsbusiness.com/api/auth/resend-2fa-code.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_id: userId }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Erreur renvoi code 2FA:', error);
    return { success: false, message: 'Erreur de connexion au serveur' };
  }
}

  return {
    user,
    loading,
    resendTwoFactorCode,
    login,
    verifyTwoFactorCode,
    cancelTwoFactor,
    logout,
    hasPermission,
    getChurchLogo,
    isAuthenticated: !!user,
    twoFactorRequired,
    twoFactorData
  };
};